import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Network Infrastructure",
  "Specialized Engineering",
  "Security & Access",
  "Networking & Fiber",
  "Hardware & Electrical",
  "Other",
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Thank you! We'll reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid-faint" />
      <div className="orb bg-[#FF6B00]/15 w-[520px] h-[520px] -top-40 right-0" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
        {/* Left content */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF6B00] mb-4">
            ◆ Contact
          </div>
          <h2 data-testid="contact-title" className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] tracking-tighter">
            Let's design the network <br />
            <span className="text-[#A0B2C6]">your business deserves.</span>
          </h2>
          <p className="mt-6 text-[#A0B2C6] text-lg leading-relaxed">
            Tell us about your project, site, or pain points. We respond within 24 hours
            with a clear plan and timeline.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href="tel:+919028442833"
              data-testid="contact-info-phone"
              className="flex items-start gap-4 group"
            >
              <div className="w-11 h-11 rounded-xl bg-[#FF6B00]/12 border border-[#FF6B00]/25 flex items-center justify-center text-[#FF6B00] shrink-0">
                <Phone size={18} strokeWidth={1.7} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A0B2C6]">Phone</div>
                <div className="font-display text-lg font-semibold mt-1 group-hover:text-[#FF6B00] transition">+91 90284 42833</div>
              </div>
            </a>

            <a
              href="mailto:hrnnetworks@gmail.com"
              data-testid="contact-info-email"
              className="flex items-start gap-4 group"
            >
              <div className="w-11 h-11 rounded-xl bg-[#FF6B00]/12 border border-[#FF6B00]/25 flex items-center justify-center text-[#FF6B00] shrink-0">
                <Mail size={18} strokeWidth={1.7} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A0B2C6]">Email</div>
                <div className="font-display text-lg font-semibold mt-1 group-hover:text-[#FF6B00] transition">hrnnetworks@gmail.com</div>
              </div>
            </a>

            <div className="flex items-start gap-4" data-testid="contact-info-address">
              <div className="w-11 h-11 rounded-xl bg-[#FF6B00]/12 border border-[#FF6B00]/25 flex items-center justify-center text-[#FF6B00] shrink-0">
                <MapPin size={18} strokeWidth={1.7} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A0B2C6]">Service area</div>
                <div className="font-display text-lg font-semibold mt-1">Maharashtra, India · Pan-India deployments</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7"
        >
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="bg-[#0B1320] border border-white/10 rounded-2xl p-7 md:p-10"
          >
            {submitted && (
              <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-3" data-testid="contact-success-banner">
                <CheckCircle2 className="text-emerald-400 shrink-0 mt-0.5" size={18} />
                <div>
                  <div className="font-semibold text-emerald-300">Message received</div>
                  <div className="text-sm text-[#A0B2C6]">Our team will get back to you within 24 hours.</div>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium text-[#A0B2C6] mb-1.5 block" htmlFor="cf-name">Name *</label>
                <input
                  id="cf-name"
                  data-testid="contact-input-name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  placeholder="Your full name"
                  className="w-full bg-[#060B14] border border-white/10 px-4 py-3 text-white placeholder:text-[#6C84A1] focus:border-[#FF6B00] focus:outline-none focus:ring-1 focus:ring-[#FF6B00] transition"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#A0B2C6] mb-1.5 block" htmlFor="cf-email">Email *</label>
                <input
                  id="cf-email"
                  data-testid="contact-input-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  placeholder="you@company.com"
                  className="w-full bg-[#060B14] border border-white/10 px-4 py-3 text-white placeholder:text-[#6C84A1] focus:border-[#FF6B00] focus:outline-none focus:ring-1 focus:ring-[#FF6B00] transition"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#A0B2C6] mb-1.5 block" htmlFor="cf-phone">Phone *</label>
                <input
                  id="cf-phone"
                  data-testid="contact-input-phone"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  required
                  placeholder="+91 9XXXXXXXXX"
                  className="w-full bg-[#060B14] border border-white/10 px-4 py-3 text-white placeholder:text-[#6C84A1] focus:border-[#FF6B00] focus:outline-none focus:ring-1 focus:ring-[#FF6B00] transition"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#A0B2C6] mb-1.5 block" htmlFor="cf-service">Interested in</label>
                <select
                  id="cf-service"
                  data-testid="contact-input-service"
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  className="w-full bg-[#060B14] border border-white/10 px-4 py-3 text-white focus:border-[#FF6B00] focus:outline-none focus:ring-1 focus:ring-[#FF6B00] transition"
                >
                  <option value="">Select a service</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-5">
              <label className="text-sm font-medium text-[#A0B2C6] mb-1.5 block" htmlFor="cf-message">Project details *</label>
              <textarea
                id="cf-message"
                data-testid="contact-input-message"
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={5}
                placeholder="Tell us about your site, scale, and goals..."
                className="w-full bg-[#060B14] border border-white/10 px-4 py-3 text-white placeholder:text-[#6C84A1] focus:border-[#FF6B00] focus:outline-none focus:ring-1 focus:ring-[#FF6B00] transition resize-none"
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <span className="text-xs text-[#6C84A1]">By submitting, you agree to be contacted by HRN Networks.</span>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#FF8533] text-white px-7 py-3.5 font-semibold tracking-wide transition-all shadow-[0_0_15px_rgba(255,107,0,0.3)] hover:shadow-[0_0_30px_rgba(255,107,0,0.5)] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send Message <Send size={16} />
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
